import { ColumnDef } from '@tanstack/solid-table';
import { getFormattedDate, getLocalizedString } from '~/i18n/utils';
import { getTranslationKeyFunction, TimeUnitsEnum } from '~/utils/commonUtils';
import { ApiConfigData, ApplicationTypeEnum } from '~/api/types/apiConfigData';
import * as zod from 'zod';
import { ReadOnlyField } from '~/components/ui/ReadOnlyField';
import ApplicationTypeContent from './ApplicationTypeContent';
import { Show } from 'solid-js';
import { Localized } from '~/i18n';
import { QueryClient } from '@tanstack/solid-query';
import { ToastContextValue } from '~/components/ui/Toast';
import { ConfirmModalValues } from '~/components/ui/Modal/ConfirmModalContext';
import { deleteApplicationById } from '~/api/resources/multiApplications';
import {
  ApiErrorInstance,
  getEnvironmentMultiApplicationListQueryKey,
  isApi5xxResponseStatus,
  withHandleError
} from '~/api';
import { HttpStatusCode } from 'axios';
import { EnvironmentPagePath, getSlug } from '~/components/AppRouter/utils';
import ApplicationActionsDropdown from './ApplicationActionsDropdown';
import InfoIcon from '~/components/ui/InfoIcon';
import Alert from '~/components/ui/Alert';

export enum MultiApplicationTabs {
  CONFIGURATION = 'configuration',
  REDIRECT = 'redirects'
}

export enum MultiApplicationFlowType {
  NEW_APPLICATION = 'new_application',
  EDIT_APPLICATION = 'edit_application'
}

export const MULTI_APPLICATION_LIST_PAGE_SIZE = 10;

const MIN_ACCESS_TOKEN_EXPIRY_IN_MINS = 1;
const MAX_ACCESS_TOKEN_EXPIRY_IN_MINS = 525600;

export const ACCESS_TOKEN_UNIT_OPTIONS = [TimeUnitsEnum.MINUTES];

export const DEFAULT_ACCESS_TOKEN_EXPIRY_UNIT = TimeUnitsEnum.MINUTES;

export const getTranslationKey = getTranslationKeyFunction(
  'pages.multi_applications'
);

export const getMultiApplicationsTableColumnDef = ({
  intl,
  onDeleteApplication,
  onEditApplication
}: {
  intl: any;
  onDeleteApplication: (data: ApiConfigData) => Promise<any>;
  onEditApplication: (data: ApiConfigData) => void;
}): ColumnDef<ApiConfigData>[] => [
  {
    header: getLocalizedString(
      getTranslationKey('table_header_application_name'),
      { intl }
    ),
    accessorKey: 'name',
    cell: (info) => (
      <div class="flex flex-col items-start gap-3 min-[1200px]:flex-row min-[1200px]:items-center">
        <span>{info.getValue() as string}</span>
        <Show
          when={
            info.row.original.clientType ===
            ApplicationTypeEnum.DEFAULT_APPLICATION
          }
        >
          <Alert
            variant={'info'}
            skipLogo
            class="m-0 !px-2 !py-0 [&>*]:m-0"
          >
            <div class="flex items-center gap-2">
              <InfoIcon
                width={12}
                class="m-0"
                tooltipContent={getLocalizedString(
                  getTranslationKey('environment_default_tooltip'),

                  { intl }
                )}
              />
              <Localized
                translationKey={getTranslationKey('environment_default')}
              />
            </div>
          </Alert>
        </Show>
      </div>
    ),
    meta: {
      class: 'w-4/12 min-[1200px]:w-5/12'
    }
  },
  {
    header: getLocalizedString(
      getTranslationKey('table_header_application_client_id'),
      { intl }
    ),
    accessorKey: 'keyId',
    cell: (info) => (
      <ReadOnlyField
        variant="filled"
        value={info.getValue() as string}
        containerClass="border-0"
      />
    ),
    meta: {
      class: 'w-4/12'
    }
  },
  {
    header: getLocalizedString(
      getTranslationKey('table_header_application_type'),
      { intl }
    ),
    accessorKey: 'clientType',
    cell: (info) => (
      <ApplicationTypeContent
        type={info.getValue() as ApplicationTypeEnum}
        showDescription={false}
        showTagList={false}
        showIcon={false}
        class="[&_*]:!font-normal"
      />
    ),
    meta: {
      class: 'w-2/12'
    }
  },
  {
    header: getLocalizedString(
      getTranslationKey('table_header_application_created_on'),
      { intl }
    ),
    accessorKey: 'createTime',
    cell: (info) =>
      getFormattedDate(info.getValue() as string, { includeTime: true }),
    meta: {
      class: 'w-3/12'
    }
  },
  {
    header: '',
    id: 'actions',
    accessorKey: 'keyId',
    cell: (info) => {
      return (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <ApplicationActionsDropdown
            appData={info.row.original}
            dropdownId={`multi-app-table-row-actions-${info.row.original.keyId}`}
            showEdit
            onEdit={onEditApplication}
            onDelete={onDeleteApplication}
            dataTestIdPrefix="multi-app-table-row-actions"
          />
        </div>
      );
    },
    meta: {
      class: 'w-1/12'
    }
  }
];

export const getZodApplicationsDetailsSchema = (intl) => {
  return zod.object({
    name: zod
      .string()
      .min(
        1,
        getLocalizedString(
          getTranslationKey('validation_message_name_required'),
          { intl }
        )
      ),
    allowApiAccess: zod.boolean(),
    enforcePKCE: zod.boolean(),
    accessTokenExpiry: zod.preprocess(
      (val) => {
        if (val === `` || val === null || val === undefined) return undefined;
        return Number(val);
      },
      zod
        .number()
        .optional()
        .refine(
          (val) =>
            val === undefined ||
            val === 0 ||
            (val >= MIN_ACCESS_TOKEN_EXPIRY_IN_MINS &&
              val <= MAX_ACCESS_TOKEN_EXPIRY_IN_MINS),
          {
            message: getLocalizedString(
              getTranslationKey('validation_message_token_exp_required_range'),
              {
                intl,
                min: MIN_ACCESS_TOKEN_EXPIRY_IN_MINS,
                max: MAX_ACCESS_TOKEN_EXPIRY_IN_MINS
              }
            )
          }
        )
    )
  });
};

export const getZodCreateNewApplicationSchema = (intl) => {
  return zod.object({
    applicationName: zod
      .string()
      .min(
        1,
        getLocalizedString(
          getTranslationKey('validation_message_name_required'),
          { intl }
        )
      ),
    applicationType: zod
      .nativeEnum(ApplicationTypeEnum, {
        errorMap: () => ({
          message: getLocalizedString(
            getTranslationKey('validation_message_application_type_required'),
            { intl }
          )
        })
      })
      .refine(Boolean, {
        message: getLocalizedString(
          getTranslationKey('validation_message_application_type_required'),
          { intl }
        )
      })
  });
};

export type ActionFunctionsArgumentsType = {
  data: ApiConfigData;
  intl: any;
  queryClient: QueryClient;
  toaster: ToastContextValue;
  confirm: ConfirmModalValues;
};

export const handleDeleteApplication: (
  args: ActionFunctionsArgumentsType
) => Promise<boolean> = ({ data, intl, queryClient, toaster, confirm }) => {
  return new Promise((resolve, reject) => {
    confirm.show({
      title: (
        <Localized
          translationKey={getTranslationKey('delete_application_confirm_title')}
          vars={{ intl }}
        />
      ),
      description: (
        <Localized
          translationKey={getTranslationKey(
            'delete_application_confirm_description'
          )}
          vars={{ intl, applicationName: data.name }}
        />
      ),
      onConfirm: async () => {
        try {
          confirm.setIsLoading(true);
          await deleteApplicationById({ applicationId: data.keyId });
          await queryClient.refetchQueries({
            queryKey: getEnvironmentMultiApplicationListQueryKey(),
            type: 'all',
            exact: true
          });
          confirm.close();
          toaster.toast().success({
            description: getLocalizedString(
              getTranslationKey('delete_application_toast_success'),
              {
                intl,
                applicationName: data.name
              }
            )
          });

          resolve(true);
        } catch (err) {
          toaster.toast().error({
            description: getLocalizedString(
              getTranslationKey('delete_application_toast_failure'),
              {
                intl,
                applicationName: data.name
              }
            )
          });
          reject(err);
        } finally {
          confirm.setIsLoading(false);
        }
      },
      onCancel: () => {
        resolve(false);
      }
    });
  });
};

export const handleEditApplicationErrors = withHandleError(
  ({ error, intl }) => {
    const serverError = error as ApiErrorInstance;
    if (
      serverError.httpStatus === HttpStatusCode.BadRequest ||
      isApi5xxResponseStatus(serverError.httpStatus)
    ) {
      return getLocalizedString(getTranslationKey('update_failed_message'), {
        intl
      });
    }
  }
);

export const generateRedirectionToApplicationListURL = (envId: string) => {
  return getSlug.environmentById(
    envId,
    EnvironmentPagePath.ENVIRONMENT_MULTI_APPLICATION,
    true
  );
};

export const getPermissionsForShowingSKI_API_Access = (
  applicationType: ApplicationTypeEnum
) => {
  if (
    applicationType === ApplicationTypeEnum.DEFAULT_APPLICATION ||
    applicationType === ApplicationTypeEnum.NATIVE_APPLICATION ||
    applicationType === ApplicationTypeEnum.SINGLE_PAGE_APPLICATION
  ) {
    return false;
  }

  return true;
};

export const getPermissionsForShowingEnforce_PKCE = (
  applicationType: ApplicationTypeEnum
) => {
  if (applicationType === ApplicationTypeEnum.DEFAULT_APPLICATION) {
    return false;
  }
  return true;
};

export const getPermissionForEditingEnforce_PKCE = (
  applicationType: ApplicationTypeEnum
) => {
  if (
    applicationType === ApplicationTypeEnum.NATIVE_APPLICATION ||
    applicationType === ApplicationTypeEnum.SINGLE_PAGE_APPLICATION
  ) {
    return false;
  }

  return true;
};
